import { ArrowLeftRight, LogOut, Menu, ShieldCheck, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import GoogleTranslate from '@/components/GoogleTranslate';
import { useAuth } from '@/contexts/AuthContext';
import { ADMIN_SUBPAGES } from '@/pages/AdminApp';

// Mirror of AdminSidebar.GOD_ONLY_SUBPAGES — kept in sync to ensure the
// mobile drawer and desktop sidebar surface exactly the same set of
// items for the same admin role.
const GOD_ONLY_SUBPAGES = new Set(['profit', 'payouts']);

export const AdminMobileTopBar = ({ active, onSelect, drawerOpen, setDrawerOpen, admin }) => {
  const navigate = useNavigate();
  const activeMeta = ADMIN_SUBPAGES.find((s) => s.id === active);
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
      <header
        className="lg:hidden fixed top-0 left-0 right-0 z-50 h-16 px-4 flex items-center justify-between bg-[rgba(8,8,9,0.92)] backdrop-blur-xl border-b border-[rgba(255,182,18,0.12)]"
        data-testid="admin-mobile-topbar"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#FFC20E] to-[#FFB612]">
            <ShieldCheck className="w-4 h-4 text-[#0a0a0b]" strokeWidth={2.6} />
          </div>
          <div className="font-display font-bold text-sm whitespace-nowrap">
            <span className="sc-gold-text">Admin</span>
          </div>
          <span className="text-[12px] text-[var(--sc-text-dim)] ml-1 truncate">/ {activeMeta?.label}</span>
        </div>
        <div className="flex items-center gap-2">
          <GoogleTranslate />
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="w-9 h-9 rounded-lg border border-[rgba(255,182,18,0.32)] flex items-center justify-center text-[#FFC20E]"
            aria-label="Open menu"
            data-testid="admin-hamburger"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </header>

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
          <aside className="absolute right-0 top-0 bottom-0 w-[280px] max-w-[88vw] bg-[rgba(8,8,9,0.96)] border-l border-[rgba(255,182,18,0.18)] backdrop-blur-xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[rgba(255,182,18,0.10)]">
              <div className="font-display font-bold">Admin Menu</div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 rounded-lg border border-[rgba(255,182,18,0.32)] flex items-center justify-center text-[#FFC20E]"
                aria-label="Close menu"
                data-testid="admin-drawer-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 py-4 border-b border-[rgba(255,182,18,0.08)]">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Signed in as</div>
              <div className="font-display font-bold mt-1">{admin?.username || '—'}</div>
              <div className="text-[11px] text-[#FFC20E]">{admin?.is_god_account ? 'GOD ADMIN' : 'ADMIN ROLE'}</div>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
              {ADMIN_SUBPAGES.map((s) => {
                if (GOD_ONLY_SUBPAGES.has(s.id) && admin && !admin.is_god_account) {
                  return null;
                }
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => onSelect(s.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl text-[14px] transition ${
                      active === s.id
                        ? 'bg-[rgba(255,182,18,0.10)] border border-[rgba(255,182,18,0.32)] text-[#FFC20E] font-semibold'
                        : 'text-[var(--sc-text-dim)] hover:text-[#FFC20E] hover:bg-[rgba(255,182,18,0.04)] border border-transparent'
                    }`}
                    data-testid={`admin-mobile-nav-${s.id}`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </nav>

            <div className="px-3 pb-4 pt-2 border-t border-[rgba(255,182,18,0.08)] space-y-1">
              <button
                type="button"
                onClick={() => navigate('/dashboard', { replace: true })}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-[13px] text-[var(--sc-text-mute)] hover:text-[#FFC20E] hover:bg-[rgba(255,182,18,0.04)] transition"
              >
                <ArrowLeftRight className="w-4 h-4" />
                Switch to Member View
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-[13px] text-[var(--sc-text-mute)] hover:text-[#FFC20E] hover:bg-[rgba(255,182,18,0.04)] transition"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default AdminMobileTopBar;
